/**
 * Nội dung form danh mục dịch vụ (3 tab). Đọc/ghi qua FormProvider.
 */
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CharacterCount,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@/shared/ui';
import { form } from '@/shared/theme';
import type { TreatmentCatalogFormData } from '../types/treatmentCatalog.types';
import {
  CATALOG_DESCRIPTION_MAX_LENGTH,
  CATALOG_DURATION_MAX_MINUTES,
  CATALOG_DURATION_MIN_MINUTES,
  CATALOG_NAME_MAX_LENGTH,
  CATALOG_PRICE_MAX,
  CATALOG_PRICE_MIN,
  type CatalogFormTab,
} from '../utils/treatmentCatalogItemForm';
import ProcedureStepsEditor from './ProcedureStepsEditor';
import MaterialLinesEditor from './MaterialLinesEditor';
import TreatmentCategoryField from './TreatmentCategoryField';
import TreatmentUomField from './TreatmentUomField';
import LabCatalogDefaultsFields from './LabCatalogDefaultsFields';
import WarrantyCatalogFields from './WarrantyCatalogFields';

const CatalogStatusOption = ({ active, label }: { active: boolean; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      aria-hidden
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: active ? 'success.main' : 'text.disabled',
        flexShrink: 0,
      }}
    />
    {label}
  </Box>
);

export interface TreatmentCatalogFormFieldsProps {
  mode: 'create' | 'edit';
  disabled?: boolean;
  catalogItemId?: string;
  activeTab: CatalogFormTab;
}

const TreatmentCatalogFormFields: React.FC<TreatmentCatalogFormFieldsProps> = ({
  mode,
  disabled = false,
  catalogItemId,
  activeTab,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const { control, watch, setValue, clearErrors, formState: { errors } } =
    useFormContext<TreatmentCatalogFormData>();

  const procedureSteps = watch('procedureSteps');
  const materialLines = watch('materialLines');
  const labOrderEnabled = watch('labOrderEnabled');

  if (activeTab === 'general') {
    return (
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label={t('fields.name')}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                placeholder={t('catalog.placeholders.name')}
                required
                disabled={disabled}
                labelEnd={
                  <CharacterCount current={field.value.length} max={CATALOG_NAME_MAX_LENGTH} />
                }
                slotProps={{
                  htmlInput: { maxLength: CATALOG_NAME_MAX_LENGTH },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="category"
            control={control}
            render={({ field, fieldState }) => (
              <TreatmentCategoryField
                value={field.value}
                onChange={(category) => {
                  field.onChange(category);
                  clearErrors('category');
                }}
                error={fieldState.error?.message}
                required
                disabled={disabled}
                includeCategoryId={mode !== 'create' ? field.value || undefined : undefined}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="price"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label={t('fields.price')}
                type="number"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                required
                disabled={disabled}
                placeholder={t('catalog.placeholders.price', {
                  min: CATALOG_PRICE_MIN.toLocaleString('vi-VN'),
                  max: CATALOG_PRICE_MAX.toLocaleString('vi-VN'),
                })}
                slotProps={{
                  htmlInput: {
                    min: CATALOG_PRICE_MIN,
                    max: CATALOG_PRICE_MAX,
                    step: 1,
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="uom"
            control={control}
            render={({ field, fieldState }) => (
              <TreatmentUomField
                value={field.value}
                onChange={(uomCode) => {
                  field.onChange(uomCode);
                  clearErrors('uom');
                }}
                error={fieldState.error?.message}
                disabled={disabled}
                includeUomCode={mode !== 'create' ? field.value || undefined : undefined}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="duration"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label={t('fields.duration')}
                type="number"
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                placeholder={t('catalog.placeholders.duration', {
                  min: CATALOG_DURATION_MIN_MINUTES.toLocaleString('vi-VN'),
                  max: CATALOG_DURATION_MAX_MINUTES.toLocaleString('vi-VN'),
                })}
                required
                disabled={disabled}
                slotProps={{
                  htmlInput: {
                    min: CATALOG_DURATION_MIN_MINUTES,
                    max: CATALOG_DURATION_MAX_MINUTES,
                    step: 1,
                  },
                }}
              />
            )}
          />
        </Grid>

        <WarrantyCatalogFields
          warrantyType={watch('warrantyType')}
          warrantyMonths={watch('warrantyMonths')}
          warrantyMonthsError={errors.warrantyMonths?.message}
          disabled={disabled}
          onChange={(patch) => {
            if (patch.warrantyType !== undefined) setValue('warrantyType', patch.warrantyType);
            if (patch.warrantyMonths !== undefined) {
              setValue('warrantyMonths', patch.warrantyMonths);
              clearErrors('warrantyMonths');
            }
          }}
        />

        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>{t('fields.status')}</InputLabel>
                <Select
                  value={field.value ? 'true' : 'false'}
                  disabled={disabled}
                  renderValue={(selected) => (
                    <CatalogStatusOption
                      active={selected === 'true'}
                      label={
                        selected === 'true'
                          ? t('categoryCatalog.status.active')
                          : t('categoryCatalog.status.inactive')
                      }
                    />
                  )}
                  onChange={(e) => field.onChange(e.target.value === 'true')}
                >
                  <MenuItem value="true">
                    <CatalogStatusOption active label={t('categoryCatalog.status.active')} />
                  </MenuItem>
                  <MenuItem value="false">
                    <CatalogStatusOption
                      active={false}
                      label={t('categoryCatalog.status.inactive')}
                    />
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <InputLabel aria-hidden sx={{ visibility: 'hidden', pointerEvents: 'none' }}>
              {'\u00a0'}
            </InputLabel>
            <Controller
              name="labOrderEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ height: form.controlHeight }}
                  control={
                    <Checkbox
                      checked={field.value}
                      disabled={disabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        field.onChange(checked);
                        if (!checked) {
                          setValue('defaultLabWorkType', '');
                          setValue('defaultLabMaterial', '');
                        }
                      }}
                    />
                  }
                  label={t('fields.labOrderEnabled')}
                />
              )}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <LabCatalogDefaultsFields
            labOrderEnabled={labOrderEnabled}
            defaultLabWorkType={watch('defaultLabWorkType')}
            defaultLabMaterial={watch('defaultLabMaterial')}
            disabled={disabled}
            onChange={(patch) => {
              if (patch.defaultLabWorkType !== undefined) {
                setValue('defaultLabWorkType', patch.defaultLabWorkType);
              }
              if (patch.defaultLabMaterial !== undefined) {
                setValue('defaultLabMaterial', patch.defaultLabMaterial);
              }
            }}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('fields.description')}
                multiline
                rows={3}
                placeholder={t('catalog.descriptionPlaceholder')}
                disabled={disabled}
                labelEnd={
                  <CharacterCount current={field.value.length} max={CATALOG_DESCRIPTION_MAX_LENGTH} />
                }
                slotProps={{
                  htmlInput: { maxLength: CATALOG_DESCRIPTION_MAX_LENGTH },
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    );
  }

  if (activeTab === 'steps') {
    return (
      <Box>
        <ProcedureStepsEditor
          steps={procedureSteps}
          disabled={disabled}
          kpiCheckEnabled
          excludeCatalogId={catalogItemId}
          onCopyFrom={({ procedureSteps: copied }) => {
            setValue('procedureSteps', copied);
            clearErrors('procedureSteps');
          }}
          onChange={(steps) => {
            setValue('procedureSteps', steps);
            clearErrors('procedureSteps');
          }}
          error={errors.procedureSteps?.message}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('catalog.materialLinesHint')}
      </Typography>
      <MaterialLinesEditor
        lines={materialLines}
        onChange={(lines) => setValue('materialLines', lines)}
        disabled={disabled}
      />
    </Box>
  );
};

export default TreatmentCatalogFormFields;
